import { Badge, Calendar, Divider, Drawer } from "antd";
import dayjs from "dayjs";
import { useState } from "react";

import {
  EnvironmentOutlined,
  CalendarOutlined,
  TeamOutlined,
  TagOutlined,
  UserOutlined,
  FileImageOutlined,
} from "@ant-design/icons";
import { format } from "date-fns";
import { VITE_API_BASE_URL } from "../../config";

const EventsCalendar = ({ eventsData }) => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setSelectedEvent(null);
    setIsDrawerOpen(false);
  };

  const dateCellRender = (value) => {
    const dateStr = value.format("YYYY-MM-DD");

    const eventsForDate = eventsData.filter((event) =>
      dayjs(event.start_date).isSame(dateStr, "day")
    );

    return (
      <ul className="events-list" style={{ listStyle: "none", paddingLeft: 0 }}>
        {eventsForDate.map((event) => (
          <li key={event.id}>
            <span
              onClick={() => handleEventClick(event)}
              className="cursor-pointer hover:underline text-blue-600 text-sm"
            >
              <Badge status="processing" text={event.title} />
            </span>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <>
      <Calendar
        cellRender={dateCellRender}
        fullscreen={false}
        className="rounded-md  shadow-sm bg-white"
      />

      <Drawer
        title={
          <div className="flex items-center gap-3">
            <TagOutlined className="text-indigo-600" />
            <span className="text-lg font-semibold text-gray-800">
              {selectedEvent?.title || "Event Details"}
            </span>
          </div>
        }
        width={550}
        onClose={closeDrawer}
        open={isDrawerOpen}
        placement="right"
      >
        {selectedEvent ? (
          <div className="text-sm text-gray-700 space-y-6 px-1">
            {/* Grid layout for metadata */}
            <div className="grid grid-cols-1 gap-y-3 sm:grid-cols-2 sm:gap-x-4">
              <div className="flex items-center gap-2">
                <EnvironmentOutlined className="text-orange-500" />
                <span>
                  <strong>Location:</strong> {selectedEvent.location || "—"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <UserOutlined className="text-green-600" />
                <span>
                  <strong>Host:</strong> {selectedEvent.host || "—"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <TagOutlined className="text-blue-600" />
                <span>
                  <strong>Status:</strong> {selectedEvent.status || "—"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CalendarOutlined className="text-indigo-500" />
                <span>
                  <strong>Start:</strong>{" "}
                  {selectedEvent.start_date
                    ? format(new Date(selectedEvent.start_date), "do MMM yyyy")
                    : "—"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <CalendarOutlined className="text-indigo-500" />
                <span>
                  <strong>End:</strong>{" "}
                  {selectedEvent.end_date
                    ? format(new Date(selectedEvent.end_date), "do MMM yyyy")
                    : "—"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <TeamOutlined className="text-rose-500" />
                <span>
                  <strong>Year:</strong> {selectedEvent.session?.name || "—"}
                </span>
              </div>
            </div>

            <Divider />

            {/* Description */}
            <div className="border border-amber-400 pb-2 bg-yellow-300/20 rounded-md px-3 py-2">
              <h4 className="text-sm font-semibold text-gray-600 mb-1">
                Description
              </h4>
              <p className="text-gray-700">
                {selectedEvent.description || "—"}
              </p>
            </div>

            {/* Registration */}
            {selectedEvent.registration_link && (
              <div>
                <h4 className="text-sm font-semibold text-gray-600 mb-1">
                  Registration
                </h4>
                <a
                  href={selectedEvent.registration_link}
                  target="_blank"
                  rel="noreferrer"
                  className="text-indigo-600 underline"
                >
                  Register Here
                </a>
              </div>
            )}

            {/* Image */}
            {selectedEvent.image && (
              <div>
                <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                  <FileImageOutlined className="text-gray-500" />
                  Event Banner
                </h4>
                <img
                  src={`${VITE_API_BASE_URL}/files/events/${selectedEvent.image}`}
                  alt="Event"
                  className="w-full h-auto rounded-md border object-cover"
                />
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-500">No event selected.</p>
        )}
      </Drawer>
    </>
  );
};

export default EventsCalendar;
